import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import BenefitDetailCard from './BenefitDetailCard';
import ClubProgressSlider from './ClubProgressSlider';
import { downloadInsuranceCardAction, downloadStaffInsuranceCardAction, StaffDetailAction } from '../APIs/action';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CustomButton, CustomSelect } from '../../../MUI_custom_component';
import { toCapitalized } from '../../../Utils/Capitalize';
import InfoRow from './InfoRow';
import IllustrationWrapper from '../../../../v3/Illustrration/IllustrationWrapper';
import NoInventorySvg from '../../../../v3/Illustrration/components/NoInventorySvg';
import Lottie from 'lottie-react';
import ProgressLottie from '../../../../utils/Lottie/progress.json';
import NotIssuedLottie from '../../../../utils/Lottie/NotIssuedStamp.json';
import { DownloadOutlined } from '../../../../components/icons';
import { trackAnalyticsEvent } from '../../../../utils/clevertap';
import { useSelector } from 'react-redux';

const StaffDetails = () => {
	const location = useLocation();
	const navigate = useNavigate();

	const insuranceType = location.state?.insuranceType;
	const selectedManufacturer = location.state?.selectedManufacturer;
	const selectedCompanyEntity = location.state?.selectedCompanyEntity;

	const [benefitDetail, setBenefitDetail] = useState([]);
	const [loader, setLoader] = useState(false);
    

	const benefitDetails = benefitDetail?.benefit_card;
	const policyDetails = benefitDetail?.policy_details || {};
	const clubProgressDetails = benefitDetail?.club_progress_details;
    const StaffList = policyDetails?.staff_list || [];
    const details = policyDetails?.details || [];

    const [selectedValue, setSelectedValue] = useState('');
    useEffect(() => {
        if (StaffList.length > 0 && !selectedValue) {
            setSelectedValue(StaffList[0].value);
        }
    }, [StaffList, selectedValue]);

    const selectedData = useMemo(() => {
        return details.find((d) => d.staff_no === selectedValue);
    }, [selectedValue, details]);

	const getBenefitDetail = async (selectedManufacturer, selectedCompanyEntity) => {
		setLoader(true);
        const { data, message, error } = await StaffDetailAction( selectedManufacturer, selectedCompanyEntity );

        setLoader(false);
        // setBenefitDetail(DummyData.data); // Remove this line when API is ready
        if (!error) {
            setBenefitDetail(data);
        } else {
            toast.error(message);
            setLoader(false);
        }
	};
	useEffect(()=>{
		if(selectedManufacturer && selectedCompanyEntity){
			getBenefitDetail(selectedManufacturer, selectedCompanyEntity);
		} else {
			navigate('/benefits/mybenefits', { replace: true });
		}
	},[ selectedManufacturer, insuranceType, selectedCompanyEntity ])
    const [isLoading, setIsLoading] = useState(false);
    const userData = useSelector(state => state.user?.userData);
    
    const handleDownloadECard = async () => {
        if (isLoading) return;
        
        // Track eCard download event
        trackAnalyticsEvent('INSURANCE_DETAILS_ECARD_DOWNLOAD_CLICK', {
          manufacturer_id: userData?.manufacturer_id || '',
          benefit_type: insuranceType || '',
          beneficiary_name: selectedValue || ''
        });
      
        try {
          setIsLoading(true);
          const downloadPromise = downloadStaffInsuranceCardAction(
            selectedCompanyEntity,
            selectedData.staff_no.replace(/^Staff\s*/i, ''), 
            'E-card.pdf'
          );
          const minDelayPromise = new Promise(resolve => setTimeout(resolve, 1000));
          
          await Promise.all([downloadPromise, minDelayPromise]);
          
          const isInWebView = window.navigator.userAgent.includes('wv') || 
                             window.navigator.standalone || 
                             window.matchMedia('(display-mode: standalone)').matches;
          
          if (isInWebView) {
            console.log('Download initiated successfully');
          }
        } catch (error) {
          console.error('Download failed:', error);
        } finally {
          setIsLoading(false);
        }
    };
	
	if (loader) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress/>
			</Box>
		);
	}
	return (
		<Box sx={{ p: { xs:2, md:3 } }}>
			<Grid container spacing={{xs:2, md:4}} sx={{ mb: 2 }}>
				<Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, background: '#ffffff', border: '1px solid #D9D9D9', borderRadius: 2 }}>
						<BenefitDetailCard benefitDetails={benefitDetails} spacing={false}/>
					</Box>
				</Grid>
				{clubProgressDetails && (
					<Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
						<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#ffffff' }}>
							<ClubProgressSlider progress={clubProgressDetails} insurance={false} />
						</Box>
					</Grid>
				)}
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column'}}>
                    {policyDetails && StaffList.length > 0 && (
                        <Box sx={{  }}>
                            {StaffList.length > 1 ? (
                                <CustomSelect
                                    fullWidth
                                    label="Staff"
                                    value={selectedValue}
                                    onChange={(e) => setSelectedValue(e.target.value)}
                                    sx={{ mb: 2 }}
                                >
                                    {StaffList.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {toCapitalized(item.label)}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            ) : StaffList.length === 1 ? (
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Staff"
                                    value={toCapitalized(StaffList[0]?.label || '')}
                                    variant="outlined"
                                    disabled
                                    sx={{ mb: 2 }}
                                />
                            ) : null}
                            
                            {selectedValue && selectedData ? (
                                <>
                                    {selectedData?.beneficiary_details ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, borderRadius: '16px', backgroundColor: '#fff' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Typography variant="h3" >
                                                    Beneficiary Details
                                                </Typography>
                                                <Box>
                                                    {selectedData.beneficiary_details.map((item, index) => (
                                                        <InfoRow key={index} 
                                                            label={item.display_name}
                                                            value={item.display_value}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                            <CustomButton
                                                variant="outlined"
                                                color="primary"
                                                label="E-Card"
                                                rightIcon={DownloadOutlined}
                                                onClick={() => {
                                                    handleDownloadECard();
                                                }}
                                                loading={isLoading}
                                            />
                                            <Box>
                                                {selectedData.nominee_detail && selectedData.nominee_detail.length > 0 && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Typography variant="h3" >
                                                            Nominee Details
                                                        </Typography>
                                                        <Box>
                                                        {selectedData.nominee_detail.map((item, index) => (
                                                            <InfoRow key={index} 
                                                                label={item.display_name}
                                                                value={item.display_value}
                                                            />
                                                        ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    ) : selectedData?.nudges ? (
                                        <>
                                            {selectedData.nudges.type === 'add' ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                                    <IllustrationWrapper
                                                        titleVariant="h3"
                                                        subtitleVariant="body1"
                                                        subtitleStyle = {{color:'#666'}}
                                                        illustration={<NoInventorySvg />}
                                                        title={selectedData.nudges.info_title && selectedData.nudges.info_title }
                                                        subtitle={selectedData.nudges.info_text && selectedData.nudges.info_text }
                                                        illustrationProps={{
                                                            width: 200,
                                                            height: 165,
                                                        }}
                                                        
                                                    />
                                                    <CustomButton
                                                        variant="contained"
                                                        color="primary"
                                                        label="Add Beneficiary"
                                                        onClick={() => {
                                                            navigate('/beneficiary/staff-details', { state: { staffNo: selectedData.staff_no, insuranceType, selectedManufacturer, selectedCompanyEntity } });
                                                        }}
                                                    />
                                                </Box>
                                            ) : selectedData.nudges.type === 'in_progress' ? (
                                                <>
                                                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                                                        <Lottie
                                                            animationData={ProgressLottie}
                                                            loop={true}
                                                            style={{ width: 200, height: 150, padding: 0 }}
                                                        />
                                                    </Box>  
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',justifyContent: 'center', width: '100%', gap: 2, }}>
                                                        {selectedData.nudges?.info_title && (
                                                            <Typography 
                                                                variant='h3' 
                                                            >
                                                                {selectedData.nudges?.info_title}
                                                            </Typography>
                                                        )}
                                                        {selectedData.nudges?.info_text && (
                                                            <Typography 
                                                                variant='body1' 
                                                                sx={{ color: '#666' }}
                                                            >
                                                                {selectedData.nudges?.info_text}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </>
                                            ) : selectedData.nudges.type === 'not_issued' ? (
                                                <>
                                                    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                                                        <Lottie
                                                            animationData={NotIssuedLottie}
                                                            loop={true}
                                                            style={{ width: 200, height: 150, padding: 0 }}
                                                        />
                                                    </Box>  
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',justifyContent: 'center', width: '100%', gap: 2, }}>
                                                        {selectedData.nudges?.info_title && (
                                                            <Typography 
                                                                variant='h3'
                                                            >
                                                                {selectedData.nudges?.info_title}
                                                            </Typography>
                                                        )}
                                                        {selectedData.nudges?.info_text && (
                                                            <Typography 
                                                                variant='body1' 
                                                                sx={{ color: '#666' }}
                                                            >
                                                                {selectedData.nudges?.info_text}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </>
                                            ) : null}
                                        </>
                                            
                                    ) : (
                                        <Box sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No data available for this staff member.
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            ) : selectedValue === '' && StaffList.length > 1 ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                        Please select a staff member to view details.
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </Grid>
			</Grid>
		</Box>
	);
};

export default StaffDetails;
